
import ImagePlaceholderComponent from "@/components/common-components/image-placeholder/ImagePlaceholderComponent";
import AssetPreviewDialog from "@/components/common-components/asset-preview-dialog/AssetPreviewDialog";

import { Button } from "@/components/ui/button.tsx";
import {
    Dialog,
    DialogContentNoClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { ENV, ImageConfig } from "@/constants";
import { cn } from "@/lib/utils";
import { useMagicPrompt } from "@/hooks/useMagicPrompt";
import { useIsMobile } from "@/hooks/use-mobile";

import { Mic, Paperclip, Wand2, Sparkles, Zap, RotateCcw, AlertCircle, Clock, CheckCircle2, Shield } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import "./CustomInputComponent.scss";
import MicModalComponent from "./MicModalComponent";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDispatch, useSelector } from "react-redux";
import { getUsersTokensUtilization } from "@/store/actions/meta.action";
import commonService from "@/services/common.service";
import { useFeatureAccess } from "@/hooks/useFeatureAccess.ts";
import { Misc } from "@/constants";

import { IRootReducerState } from "@/store/reducers";
import { NetworkError } from "@/store/actions/projectdata.action";


interface CustomInputComponentProps {
    onSend: (message: string, imageFile: File | null, selectedNetworkErrors?: NetworkError[]) => void;
    loading?: boolean;
    isNew?: boolean;
    selectedFiles?: any[];
    value: string;
    onInputChange: (value: string) => void;
    onFileRemove: (index: number) => void;
    projectId?: string;
    imageFile: File | null;
    onImageFileChange: (file: File | null) => void;
    isRequirementGathering?: boolean;
    hideFileAttachment?: boolean;
    hideEnhancement?: boolean;
    onAdvancedModeChange?: (enabled: boolean) => void;
    onSessionIdChange?: (sessionId: string | null) => void;
}

const CustomInputComponent = ({
    onSend,
    loading = false,
    selectedFiles,
    value,
    onInputChange,
    onFileRemove,
    projectId,
    imageFile,
    onImageFileChange,
    isRequirementGathering = false,
    hideFileAttachment = false,
    hideEnhancement = false,
    onAdvancedModeChange,
    onSessionIdChange,
}: CustomInputComponentProps) => {
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    // Initialize advanced mode from localStorage (per project)
    const getAdvancedModeStorageKey = useCallback(() => {
        return projectId ? `${Misc.LOCAL_STORAGE_ADVANCED_MODE_PREFIX}${projectId}` : null;
    }, [projectId]);

    const [isAdvancedModeEnabled, setIsAdvancedModeEnabled] = useState(() => {
        const storageKey = projectId ? `${Misc.LOCAL_STORAGE_ADVANCED_MODE_PREFIX}${projectId}` : null;
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            return stored === 'true';
        }
        return false;
    });

    const [isContinueSessionEnabled, setIsContinueSessionEnabled] = useState(() => {
        const storageKey = projectId ? `${Misc.LOCAL_STORAGE_CONTINUE_SESSION_PREFIX}${projectId}` : null;
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            return stored === 'false' ? false : true;
        }
        return true;
    });
    const [isMicModalOpen, setIsMicModalOpen] = useState(false);
    const [isAdvancedModeDialogOpen, setIsAdvancedModeDialogOpen] = useState(false);

    // State for pending message (when showing advanced mode prompt)
    const [pendingMessage, setPendingMessage] = useState<string | null>(null);
    const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
    const [pendingNetworkErrors, setPendingNetworkErrors] = useState<NetworkError[] | undefined>(undefined);
    const [shouldSendAfterModeChange, setShouldSendAfterModeChange] = useState(false);

    // Track if user dismissed the advanced mode prompt for this project
    const [isAdvancedModePromptDismissed, setIsAdvancedModePromptDismissed] = useState(() => {
        const storageKey = projectId ? `${Misc.LOCAL_STORAGE_ADVANCED_MODE_PROMPT_DISMISSED}${projectId}` : null;
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            return stored === 'true';
        }
        return false;
    });

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [previewAsset, setPreviewAsset] = useState<{ src?: string; file?: any; alt?: string } | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isEnhancementMode, setIsEnhancementMode] = useState(false);
    const [originalPosition, setOriginalPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

    // Network errors state
    const networkErrors = useSelector((state: IRootReducerState) => state.projectData.networkErrors);
    const [isNetworkErrorsOpen, setIsNetworkErrorsOpen] = useState(false);
    const [selectedNetworkErrors, setSelectedNetworkErrors] = useState<Set<string>>(new Set());
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);

    // Auto-select all network errors by default when they change
    useEffect(() => {
        if (networkErrors && networkErrors.length > 0) {
            const allErrorIds = new Set(networkErrors.map(err => err.id));
            setSelectedNetworkErrors(allErrorIds);
        } else {
            setSelectedNetworkErrors(new Set());
        }
    }, [networkErrors]);
    const lastScrollPosition = useRef<number>(0);
    const { isProTier, isStartupTier, isEnterpriseTier } = useFeatureAccess();
    const hasAdvancedModeAccess = ENV.ENV_MODE === "development" ? true : (isProTier || isStartupTier || isEnterpriseTier || true);

    const { enhancePrompt, isProcessing } = useMagicPrompt({
        onPromptUpdate: onInputChange
    });

    // Persist advanced mode state to localStorage whenever it changes
    useEffect(() => {
        const storageKey = getAdvancedModeStorageKey();
        if (storageKey) {
            localStorage.setItem(storageKey, isAdvancedModeEnabled.toString());
        }
    }, [isAdvancedModeEnabled, getAdvancedModeStorageKey]);

    // Persist continue session state to localStorage whenever it changes
    useEffect(() => {
        const storageKey = projectId ? `${Misc.LOCAL_STORAGE_CONTINUE_SESSION_PREFIX}${projectId}` : null;
        if (storageKey) {
            localStorage.setItem(storageKey, isContinueSessionEnabled.toString());
        }
    }, [isContinueSessionEnabled, projectId]);

    // Load advanced mode state when projectId changes
    useEffect(() => {
        const storageKey = projectId ? `${Misc.LOCAL_STORAGE_ADVANCED_MODE_PREFIX}${projectId}` : null;
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            const shouldBeEnabled = stored === 'true';
            setIsAdvancedModeEnabled(shouldBeEnabled);
            onAdvancedModeChange?.(shouldBeEnabled);
        } else {
            // Reset to false if no projectId
            setIsAdvancedModeEnabled(false);
            onAdvancedModeChange?.(false);
        }

        // Load dismissal state for this project
        const dismissalKey = projectId ? `${Misc.LOCAL_STORAGE_ADVANCED_MODE_PROMPT_DISMISSED}${projectId}` : null;
        if (dismissalKey) {
            const dismissalStored = localStorage.getItem(dismissalKey);
            setIsAdvancedModePromptDismissed(dismissalStored === 'true');
        } else {
            setIsAdvancedModePromptDismissed(false);
        }
    }, [projectId, onAdvancedModeChange]);

    // Load continue session state when projectId changes
    useEffect(() => {
        const storageKey = projectId ? `${Misc.LOCAL_STORAGE_CONTINUE_SESSION_PREFIX}${projectId}` : null;
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            const shouldBeEnabled = stored === 'false' ? false : true;
            setIsContinueSessionEnabled(shouldBeEnabled);
            // Get session ID from localStorage and notify parent
            const sessionId = localStorage.getItem(Misc.LOCAL_STORAGE_CLAUDE_SESSION_ID);
            if (shouldBeEnabled && sessionId) {
                onSessionIdChange?.(sessionId);
            } else {
                onSessionIdChange?.(null);
            }
        } else {
            setIsContinueSessionEnabled(true);
            const sessionId = localStorage.getItem(Misc.LOCAL_STORAGE_CLAUDE_SESSION_ID);
            onSessionIdChange?.(sessionId || null);
        }
    }, [projectId, onSessionIdChange]);

    const handleTranscriptChange = useCallback((newTranscript: string) => {
        onInputChange(newTranscript);
    }, [onInputChange]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onInputChange(e.target.value);
    };

    // Close enhancement mode manually
    const handleCloseEnhancementMode = useCallback(() => {
        setIsEnhancementMode(false);
        // Clear original position after animation completes
        setTimeout(() => {
            setOriginalPosition(null);
        }, 600); // Match the transition duration
    }, []);

    // Actually send the message (called after mode selection or if no prompt needed)
    const doSend = useCallback((messageToSend: string, imageToSend: File | null, errorsToSend?: NetworkError[]) => {
        onSend(messageToSend, imageToSend, errorsToSend);

        // Clear pending state
        setPendingMessage(null);
        setPendingImageFile(null);
        setPendingNetworkErrors(undefined);

        // Clear selected network errors after sending
        setSelectedNetworkErrors(new Set());

        // Close enhancement mode after sending if it's open
        if (isEnhancementMode) {
            handleCloseEnhancementMode();
        }
    }, [onSend, isEnhancementMode, handleCloseEnhancementMode]);

    // Effect to send message after advanced mode change propagates
    useEffect(() => {
        if (shouldSendAfterModeChange && isAdvancedModeEnabled && pendingMessage !== null) {
            // Mode change has propagated, now send the message
            doSend(pendingMessage, pendingImageFile, pendingNetworkErrors);
            setShouldSendAfterModeChange(false);
        }
    }, [shouldSendAfterModeChange, isAdvancedModeEnabled, pendingMessage, pendingImageFile, pendingNetworkErrors, doSend]);

    const handleSend = useCallback((ignoreLoading?: boolean) => {
        if (!loading || ignoreLoading && (value?.trim() || imageFile)) {
            // Get selected network errors
            const selectedErrors = networkErrors.filter(err => selectedNetworkErrors.has(err.id));

            // Check if we should show advanced mode prompt
            const shouldShowPrompt = !isRequirementGathering && // Don't show in requirement gathering
                !isAdvancedModeEnabled && // Only show if not already in advanced mode
                !isAdvancedModePromptDismissed && // Don't show if user dismissed it
                hasAdvancedModeAccess; // Only show if user has access

            if (shouldShowPrompt) {
                // Store the message and show the prompt
                setPendingMessage(value);
                setPendingImageFile(imageFile);
                setPendingNetworkErrors(selectedErrors.length > 0 ? selectedErrors : undefined);
                setIsAdvancedModeDialogOpen(true);

                commonService.handleMixpanelTrack("advanced_mode_prompt_shown_on_send", {
                    project_id: projectId,
                    has_message: !!value?.trim(),
                    has_image: !!imageFile
                });
            } else {
                // Send normally
                doSend(value, imageFile, selectedErrors.length > 0 ? selectedErrors : undefined);
            }
        }
    }, [loading, value, imageFile, networkErrors, selectedNetworkErrors, isRequirementGathering, isAdvancedModeEnabled, isAdvancedModePromptDismissed, hasAdvancedModeAccess, projectId, doSend]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // In enhancement mode after processing, Enter should close the dialog
            if (isEnhancementMode && !isProcessing && event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleCloseEnhancementMode();
                return;
            }

            if (isRequirementGathering && event.key === "Enter" && !event.shiftKey) {
                if (isMobile) return;
                event.preventDefault();
                handleSend(true);
            }
            else if (!loading && event.key === "Enter" && !event.shiftKey) {
                if (isMobile) return;
                event.preventDefault();
                handleSend();
            }
        },
        [handleSend, loading, isRequirementGathering, isEnhancementMode, isProcessing, handleCloseEnhancementMode, isMobile]
    );


    const handleImageUploadClick = () => {
        imageInputRef.current?.click();
    };


    const handleImageFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            onImageFileChange(file);

        }
        setIsPopoverOpen(false);
        if (event.target) {
            event.target.value = '';
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);

    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            onImageFileChange(file);
            // Track drag and drop file upload
            commonService.handleMixpanelTrack(
                isRequirementGathering
                    ? "file_dropped_in_requirement_gathering"
                    : "file_dropped_in_app_builder",
                {
                    file_type: file.type,
                    file_size: file.size,
                    file_name: file.name,
                    upload_method: "drag_drop"
                }
            );
            e.dataTransfer.clearData();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        // Check if there are image files in clipboard
        const hasImageFiles = e.clipboardData.files.length > 0 &&
            Array.from(e.clipboardData.files).some(file => file.type.startsWith('image/'));

        if (hasImageFiles) {
            // Prevent default text paste behavior when there are images
            e.preventDefault();
            const file = e.clipboardData.files[0];
            onImageFileChange(file);
            // Track paste file upload
            commonService.handleMixpanelTrack(
                isRequirementGathering
                    ? "file_pasted_in_requirement_gathering"
                    : "file_pasted_in_app_builder",
                {
                    file_type: file.type,
                    file_size: file.size,
                    file_name: file.name,
                    upload_method: "paste"
                }
            );
        }
        // If no image files, allow normal text paste behavior
    }

    const handleAssetPreview = (asset: { src?: string; file?: any; alt?: string }) => {
        setPreviewAsset(asset);
        setIsPreviewOpen(true);
        // Track asset preview
        commonService.handleMixpanelTrack(
            isRequirementGathering
                ? "asset_preview_opened_in_requirement_gathering"
                : "asset_preview_opened_in_app_builder",
            {
                asset_type: asset.file?.type || "unknown",
                asset_size: asset.file?.size || 0,
                asset_name: asset.file?.name || asset.alt || "unknown"
            }
        );
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setPreviewAsset(null);

    };

    const handlePromptEnhancement = useCallback(async () => {
        if (!projectId || !value.trim() || isProcessing) return;

        const payload = {
            prompt: value.trim()
        };

        await enhancePrompt(projectId, payload);
        dispatch(getUsersTokensUtilization())

    }, [projectId, value, enhancePrompt, isProcessing, dispatch]);


    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    }, [value]);

    // Auto-scroll to bottom when prompt enhancement is in progress
    useEffect(() => {
        if (isProcessing && inputRef.current && autoScrollEnabled) {
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
            lastScrollPosition.current = inputRef.current.scrollHeight;
        }
    }, [isProcessing, value, autoScrollEnabled]);

    // Detect manual scroll during enhancement
    const handleTextareaScroll = useCallback(() => {
        if (isProcessing && inputRef.current && autoScrollEnabled) {
            const currentScrollTop = inputRef.current.scrollTop;
            const scrollHeight = inputRef.current.scrollHeight;
            const clientHeight = inputRef.current.clientHeight;

            // Check if user manually scrolled up (not at the bottom)
            const isAtBottom = scrollHeight - currentScrollTop - clientHeight < 10;

            if (!isAtBottom) {
                // User scrolled up manually, disable auto-scroll
                setAutoScrollEnabled(false);
            }
        }
    }, [isProcessing, autoScrollEnabled]);

    // Handle enhancement mode transition
    useEffect(() => {
        if (isProcessing) {
            // Reset auto-scroll when starting enhancement
            setAutoScrollEnabled(true);

            // Capture the original position before moving
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setOriginalPosition({
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height
                });
            }

            // Small delay to let React render, then trigger the animation
            const timer = setTimeout(() => {
                setIsEnhancementMode(true);
            }, 50);
            return () => clearTimeout(timer);
        }
        // Note: Don't auto-close when processing stops - let user manually close or send
    }, [isProcessing]);

    // Handle backdrop click to close
    const handleBackdropClick = useCallback(() => {
        if (!isProcessing) {
            handleCloseEnhancementMode();
        }
    }, [isProcessing, handleCloseEnhancementMode]);


    return (
        <>

            <input
                type="file"
                ref={imageInputRef}
                onChange={handleImageFileSelected}
                className="hidden"
                accept="image/png, image/jpeg, image/gif, image/webp"
            />

            {/* Backdrop overlay when in enhancement mode */}
            {isEnhancementMode && (
                <div
                    className="enhancement-backdrop"
                    onClick={handleBackdropClick}
                />
            )}

            <div
                ref={containerRef}
                className={cn(
                    "custom-input-container transition-all duration-300",
                    {
                        "loading": !(isProcessing || loading),
                        "drop-zone": isDragOver,
                        "enhancing": isProcessing,
                        "enhancement-mode": isEnhancementMode
                    }
                )}
                style={
                    originalPosition && !isEnhancementMode
                        ? {
                            position: 'fixed',
                            top: `${originalPosition.top}px`,
                            left: `${originalPosition.left}px`,
                            width: `${originalPosition.width}px`,
                            height: `${originalPosition.height}px`,
                        }
                        : undefined
                }
                onDragEnter={handleDragEnter}
                onDragOver={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isDragOver && <div className="drop-overlay">Drop image to upload</div>}

                <div
                    className={cn(
                        "custom-input relative w-full border-input rounded-md border bg-[var(--input-background)] p-4 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 h-auto overflow-hidden"
                    )}
                >
                    <div className="relative ">
                        {/* Hide assets/images when enhancement mode is open */}
                        {
                            ((selectedFiles && selectedFiles.length > 0) || imageFile) && !isEnhancementMode && !isProcessing &&
                            <div className={'mb-4 flex gap-2 flex-wrap'}>
                                {
                                    selectedFiles?.map((file: any, index: number) => {
                                        return (
                                            <ImagePlaceholderComponent
                                                key={index}
                                                src={file?.url}
                                                file={file}
                                                size="md"
                                                onRemove={() => {
                                                    if (onFileRemove) {
                                                        onFileRemove(index);
                                                        // Track file removal
                                                        commonService.handleMixpanelTrack(
                                                            isRequirementGathering
                                                                ? "file_removed_in_requirement_gathering"
                                                                : "file_removed_in_app_builder",
                                                            {
                                                                file_index: index,
                                                                file_type: file?.type || "unknown",
                                                                file_size: file?.size || 0,
                                                                file_name: file?.name || "unknown"
                                                            }
                                                        );
                                                    }
                                                }}
                                                onClick={() => handleAssetPreview({ src: file?.url, file, alt: file?.name })}
                                            />
                                        )
                                    })
                                }
                                {imageFile && (
                                    <ImagePlaceholderComponent
                                        src={URL.createObjectURL(imageFile)}
                                        alt="preview"
                                        size="lg"
                                        file={imageFile}
                                        onRemove={() => {
                                            onImageFileChange(null);
                                            // Track image file removal
                                            commonService.handleMixpanelTrack(
                                                isRequirementGathering
                                                    ? "image_file_removed_in_requirement_gathering"
                                                    : "image_file_removed_in_app_builder",
                                                {
                                                    file_type: imageFile?.type || "unknown",
                                                    file_size: imageFile?.size || 0,
                                                    file_name: imageFile?.name || "unknown"
                                                }
                                            );
                                        }}
                                        onClick={() => handleAssetPreview({ src: URL.createObjectURL(imageFile), file: imageFile, alt: imageFile.name })}
                                    />
                                )}
                            </div>
                        }

                        <div className="relative">
                            <div className="editor-wrapper">

                                <textarea
                                    ref={inputRef}
                                    value={value}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    onPaste={handlePaste}
                                    onScroll={handleTextareaScroll}
                                    placeholder={isProcessing ? "Enhancing your prompt..." : "Type your instructions here..."}
                                    className={cn(
                                        "w-full bg-[var(--input-background)] border-none outline-none resize-none min-h-[2rem] overflow-y-auto peer relative z-10",
                                        // Base max-height with smooth transition
                                        "max-h-[4rem] transition-all duration-[600ms] ease-in-out",
                                        // Double the height when enhancing
                                        isProcessing && "!max-h-[8rem] bg-primary/5 border-primary/20 shadow-[0_0_0_1px_rgba(var(--primary),0.2)]",
                                        // Full expansion in enhancement mode - responsive heights
                                        isEnhancementMode && "!max-h-[230px] md:!max-h-[280px] lg:!max-h-[330px] text-base leading-relaxed"
                                    )}
                                    rows={1}
                                    disabled={isProcessing}
                                />

                                {/* Enhancement Status Overlay */}
                                {isProcessing && (
                                    <div className="absolute top-2 right-2 flex items-center gap-1.5 text-xs text-primary bg-primary/10 px-2 py-1 rounded-md shadow-sm z-50">
                                        <Sparkles className="w-3 h-3 animate-pulse" />
                                        <span className="font-medium">Enhancing...</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-2 items-center justify-between pt-1">
                            <div className="flex gap-2">
                                {/* Hide file attachment when enhancement mode is open */}
                                {
                                    !hideFileAttachment && !isEnhancementMode && !isProcessing && (

                                        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} >
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant={"outline"}
                                                    mixpanelEvent={{ name: isRequirementGathering ? "file_attachment_clicked_in_requirement_gathering" : "file_attachment_clicked_in_app_builder" }}
                                                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium"
                                                >
                                                    <Paperclip width={14} height={14} strokeWidth={1} />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="assets-popover-container">
                                                <div className="flex flex-col gap-2">
                                                    <Button
                                                        variant="secondaryOutline"
                                                        mixpanelEvent={{ name: isRequirementGathering ? "upload_assets_clicked_in_requirement_gathering" : "upload_assets_clicked_in_app_builder" }}
                                                        onClick={() => {
                                                            commonService._communications.toggleFileUploadSubject.next(true);
                                                            setIsPopoverOpen(false);
                                                        }}
                                                    >
                                                        Upload Assets
                                                    </Button>
                                                    <Button
                                                        variant="secondaryOutline"
                                                        mixpanelEvent={{ name: "upload_image_clicked_in_app_builder" }}
                                                        onClick={handleImageUploadClick}
                                                    >
                                                        Upload an Image
                                                    </Button>
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    )}
                                {/* Magic Wand Button */}
                                {!hideEnhancement && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant={'outline'}
                                                    onClick={handlePromptEnhancement}
                                                    disabled={!value.trim() || isProcessing}
                                                    mixpanelEvent={{ name: isRequirementGathering ? "prompt_enhancement_clicked_in_requirement_gathering" : "prompt_enhancement_clicked_in_app_builder" }}
                                                    className={cn(
                                                        "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-all duration-300 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] cursor-pointer disabled:cursor-not-allowed border text-[var(--custom-input-button-color)] shadow-xs bg-transparent transition-all duration-300 hover:border-[var(--custom-button-highlight-border)] hover:text-primary rounded-md text-sm gap-1.5 px-2 py-[6px] has-[svg]:px-2.5",
                                                        // Glow effect when user has typed 3+ characters
                                                        value.trim().length >= 3 && !isProcessing && "border-primary shadow-[0_0_8px_rgba(var(--primary),0.3)] hover:shadow-[0_0_12px_rgba(var(--primary),0.4)]",
                                                        // Enhanced loading state
                                                        isProcessing && "border-primary shadow-[0_0_8px_rgba(var(--primary),0.5)] animate-pulse bg-primary/5"
                                                    )}
                                                >
                                                    {isProcessing ? (
                                                        <div className="flex items-center gap-1">
                                                            <Sparkles
                                                                width={14}
                                                                height={14}
                                                                className="text-primary animate-pulse drop-shadow-[0_0_6px_rgba(var(--primary),0.8)]"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <Wand2
                                                            width={14}
                                                            height={14}
                                                            strokeWidth={1}
                                                            className={cn(
                                                                "transition-all duration-300",
                                                                value.trim().length >= 3 && !isProcessing && "text-primary drop-shadow-[0_0_8px_rgba(var(--primary),1)] shadow-[0_0_12px_rgba(var(--primary),0.6)] filter"
                                                            )}
                                                        />
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs">
                                                <div className="space-y-1">
                                                    <p className="text-sm font-medium">
                                                        {value.trim().length < 3
                                                            ? "Type 3+ characters to enable enhancement"
                                                            : isProcessing
                                                                ? "AI Enhancement in Progress"
                                                                : "Enhance Prompt with AI"
                                                        }
                                                    </p>
                                                    {isProcessing && (
                                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                            <div className="flex gap-1">
                                                                <div className="w-1 h-1 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                                                                <div className="w-1 h-1 bg-primary rounded-full animate-pulse" style={{ animationDelay: '200ms' }}></div>
                                                                <div className="w-1 h-1 bg-primary rounded-full animate-pulse" style={{ animationDelay: '400ms' }}></div>
                                                            </div>
                                                            <span>Analyzing and improving your prompt...</span>
                                                        </div>
                                                    )}
                                                    {value.trim().length >= 3 && !isProcessing && (
                                                        <p className="text-xs text-muted-foreground">
                                                            Click to make your prompt more detailed and effective
                                                        </p>
                                                    )}
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                                {/* Advanced Mode Toggle - Hide when enhancement mode is open, not in requirement gathering */}
                                {hasAdvancedModeAccess && !isEnhancementMode && !isProcessing && !isRequirementGathering && (
                                    <>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant={isAdvancedModeEnabled ? "default" : "outline"}
                                                        onClick={() => {
                                                            if (isAdvancedModeEnabled) {
                                                                // Directly disable advanced mode
                                                                setIsAdvancedModeEnabled(false);
                                                                onAdvancedModeChange?.(false);
                                                                setIsContinueSessionEnabled(false);
                                                                onSessionIdChange?.(null);
                                                                commonService.handleMixpanelTrack("advanced_mode_toggled", {
                                                                    project_id: projectId,
                                                                    enabled: false,
                                                                    location: "chat_input"
                                                                });
                                                            } else {
                                                                // Show confirmation dialog before enabling
                                                                setIsAdvancedModeDialogOpen(true);
                                                                commonService.handleMixpanelTrack("advanced_mode_dialog_opened", {
                                                                    project_id: projectId,
                                                                    location: "chat_input"
                                                                });
                                                            }
                                                        }}
                                                        className={cn(
                                                            "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] cursor-pointer disabled:cursor-not-allowed border text-[var(--custom-input-button-color)] shadow-xs bg-transparent rounded-md transition-colors hover:border-[var(--custom-button-highlight-border)] hover:text-primary rounded-md text-sm gap-1.5 px-2 py-[6px] has-[svg]:px-2.5 transition-all duration-200",
                                                            isAdvancedModeEnabled && "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                                                        )}
                                                    >
                                                        <Zap width={14} height={14} strokeWidth={1.5} className={isAdvancedModeEnabled ? "text-primary-foreground" : ""} />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">
                                                    <p>{isAdvancedModeEnabled ? "Disable Advanced Mode" : "Enable Advanced Mode"}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        {/* New Session Button - Only show when advanced mode is enabled */}
                                        {isAdvancedModeEnabled && (
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            onClick={() => {
                                                                setIsContinueSessionEnabled(false);
                                                                onSessionIdChange?.(null);
                                                                commonService._alert.showToast('Memory has refreshed', 'success');
                                                                commonService.handleMixpanelTrack("new_session_started", {
                                                                    project_id: projectId,
                                                                    location: "chat_input"
                                                                });
                                                            }}
                                                            className={cn(
                                                                "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] cursor-pointer disabled:cursor-not-allowed border text-[var(--custom-input-button-color)] shadow-xs bg-transparent rounded-md transition-colors hover:border-[var(--custom-button-highlight-border)] hover:text-primary rounded-md text-sm gap-1.5 px-2 py-[6px] has-[svg]:px-2.5 transition-all duration-200"
                                                            )}
                                                        >
                                                            <RotateCcw width={14} height={14} strokeWidth={1.5} />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">
                                                        <p>Refresh Memory</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </>
                                )}
                                {/* Network Errors Button - Show when there are network errors */}
                                {!isEnhancementMode && !isProcessing && networkErrors && networkErrors.length > 0 && (
                                    <Popover open={isNetworkErrorsOpen} onOpenChange={setIsNetworkErrorsOpen}>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            variant={'outline'}
                                                            className={cn(
                                                                "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] cursor-pointer disabled:cursor-not-allowed border border-input text-[var(--custom-input-button-color)] shadow-xs bg-transparent rounded-md transition-colors hover:border-[var(--custom-button-highlight-border)] hover:text-primary rounded-md text-sm gap-1.5 px-2 py-[6px] has-[svg]:px-2.5 transition-all duration-200 relative",
                                                                selectedNetworkErrors.size > 0 && "border-orange-500 text-orange-500"
                                                            )}
                                                        >
                                                            <AlertCircle width={14} height={14} strokeWidth={1.5} className={selectedNetworkErrors.size > 0 ? "text-orange-500" : ""} />
                                                            {selectedNetworkErrors.size > 0 && (
                                                                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                                                    {selectedNetworkErrors.size}
                                                                </span>
                                                            )}
                                                        </Button>
                                                    </PopoverTrigger>
                                                </TooltipTrigger>
                                                <TooltipContent side="top">
                                                    <p>Network Errors ({networkErrors.length})</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        <PopoverContent className="w-[500px] max-h-[400px] overflow-y-auto" align="end">
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="font-semibold text-sm">Network Errors</h4>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                const allIds = new Set(networkErrors.map(err => err.id));
                                                                setSelectedNetworkErrors(allIds);
                                                            }}
                                                            className="h-7 text-xs"
                                                        >
                                                            Select All
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => setSelectedNetworkErrors(new Set())}
                                                            className="h-7 text-xs"
                                                        >
                                                            Clear
                                                        </Button>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    Select errors to include in your message to the AI
                                                </p>
                                                <div className="space-y-2">
                                                    {networkErrors.map((error) => (
                                                        <div
                                                            key={error.id}
                                                            className={cn(
                                                                "p-3 border rounded-md cursor-pointer transition-colors hover:bg-accent",
                                                                selectedNetworkErrors.has(error.id) && "border-orange-500 bg-orange-500/10"
                                                            )}
                                                            onClick={() => {
                                                                const newSelected = new Set(selectedNetworkErrors);
                                                                if (newSelected.has(error.id)) {
                                                                    newSelected.delete(error.id);
                                                                } else {
                                                                    newSelected.add(error.id);
                                                                }
                                                                setSelectedNetworkErrors(newSelected);
                                                            }}
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedNetworkErrors.has(error.id)}
                                                                    onChange={() => { }}
                                                                    className="mt-0.5"
                                                                />
                                                                <div className="flex-1 space-y-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-xs font-mono font-semibold text-orange-500">
                                                                            {error.method}
                                                                        </span>
                                                                        {error.status && (
                                                                            <span className="text-xs font-mono text-red-500">
                                                                                {error.status}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-xs font-mono break-all text-muted-foreground">
                                                                        {error.url}
                                                                    </p>
                                                                    {error.error && (
                                                                        <p className="text-xs text-red-500 mt-1">
                                                                            {error.error}
                                                                        </p>
                                                                    )}
                                                                    {error.response && (
                                                                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                                            {typeof error.response === 'string'
                                                                                ? error.response
                                                                                : JSON.stringify(error.response)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                {/* Microphone Button - Hide when enhancement mode is open */}
                                {!isEnhancementMode && !isProcessing && (
                                    <Button
                                        variant={'outline'}
                                        onClick={() => setIsMicModalOpen(true)}
                                        mixpanelEvent={{ name: isRequirementGathering ? "dictate_mode_clciked_in_requirement_gathering" : "dictate_mode_clciked_in_app_builder" }}
                                        className={cn(
                                            "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-base font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] cursor-pointer disabled:cursor-not-allowed border border-input text-[var(--custom-input-button-color)] shadow-xs bg-transparent rounded-md transition-colors hover:border-[var(--custom-button-highlight-border)] hover:text-primary rounded-md text-sm gap-1.5 px-2 py-[6px] has-[svg]:px-2.5 transition-all duration-200"
                                        )}
                                    >
                                        <Mic width={14} height={14} strokeWidth={1} />
                                    </Button>
                                )}

                            </div>
                            {/* Show Continue button in enhancement mode after processing, otherwise Send button */}
                            {isEnhancementMode && !isProcessing ? (
                                <Button
                                    onClick={handleCloseEnhancementMode}
                                    mixpanelEvent={{ name: isRequirementGathering ? "enhancement_continue_clicked" : "enhancement_continue_clicked_in_app_builder" }}
                                    variant={'icon'}
                                    className="gap-2"
                                >
                                    <span className="text-sm font-medium">Continue</span>
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => handleSend()}
                                    disabled={(loading && !isRequirementGathering) || !value.trim() || isProcessing}
                                    variant={'icon'}
                                >
                                    <ImageConfig.SendIcon />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mic Modal */}
            <MicModalComponent
                isOpen={isMicModalOpen}
                onClose={() => setIsMicModalOpen(false)}
                onTranscriptChange={handleTranscriptChange}
                projectId={projectId}
            />

            {/* Asset Preview Dialog */}
            <AssetPreviewDialog
                isOpen={isPreviewOpen}
                onClose={handleClosePreview}
                asset={previewAsset}
            />

            {/* Advanced Mode Confirmation Dialog */}
            <Dialog
                open={isAdvancedModeDialogOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        // Dialog is being closed - clear pending state
                        setPendingMessage(null);
                        setPendingImageFile(null);
                        setPendingNetworkErrors(undefined);
                    }
                    setIsAdvancedModeDialogOpen(open);
                }}
            >
                <DialogContentNoClose className={cn(
                    "p-0 overflow-hidden border-primary/20",
                    isMobile ? "max-w-[90vw] rounded-xl" : "sm:max-w-[480px]"
                )}>
                    {/* Header with gradient background */}
                    <div className={cn(
                        "bg-gradient-to-br from-primary/15 via-primary/8 to-transparent",
                        isMobile ? "p-4 pb-3" : "p-6 pb-4"
                    )}>
                        <DialogHeader className={cn("gap-3", isMobile && "space-y-0")}>
                            <div className="flex items-center gap-4">
                                <div className={cn(
                                    "flex items-center justify-center rounded-xl bg-primary/20 border border-primary/30 shadow-lg shadow-primary/10 shrink-0",
                                    isMobile ? "w-10 h-10" : "w-12 h-12"
                                )}>
                                    <Zap className={cn("text-primary", isMobile ? "w-5 h-5" : "w-6 h-6")} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                        <DialogTitle className={cn(
                                            "font-semibold text-foreground truncate",
                                            isMobile ? "text-lg" : "text-xl"
                                        )}>
                                            Enable Advanced Mode?
                                        </DialogTitle>
                                        {/* Recommendation badge - Hide on mobile */}
                                        <div className={cn(
                                            "items-center gap-1.5 text-xs bg-primary/15 border border-primary/25 rounded-md px-2 py-1 shrink-0",
                                            isMobile ? "hidden" : "flex"
                                        )}>
                                            <Sparkles className="w-3 h-3 text-primary shrink-0" />
                                            <span className="text-primary font-medium">Recommended</span>
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5 uppercase tracking-wider font-medium truncate">
                                        Enhanced AI Processing • 5-10 min
                                    </p>
                                </div>
                            </div>
                        </DialogHeader>
                    </div>

                    {/* Content */}
                    <div className={cn(
                        isMobile ? "px-4 pb-4 pt-2" : "px-6 pb-6 pt-2"
                    )}>
                        {!isMobile && (
                            <DialogDescription className="text-sm text-muted-foreground mb-5 leading-relaxed">
                                Advanced Mode uses enhanced AI processing to deliver superior results. Here's what to expect:
                            </DialogDescription>
                        )}

                        {/* Benefits list */}
                        <div className={cn("mb-6", isMobile ? "space-y-2" : "space-y-3")}>
                            {/* Mobile: Compact single row or minimalist list */}
                            {isMobile ? (
                                <div className="grid grid-cols-1 gap-2">
                                    <div className="flex items-center gap-2 text-xs text-foreground bg-amber-500/10 p-2 rounded-md border border-amber-500/20">
                                        <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                        <span>Takes 5-10 Minutes</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-foreground bg-green-500/10 p-2 rounded-md border border-green-500/20">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                                        <span>Higher Quality Output</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-foreground bg-blue-500/10 p-2 rounded-md border border-blue-500/20">
                                        <Shield className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                        <span>Fewer Errors</span>
                                    </div>
                                </div>
                            ) : (
                                /* Desktop: Detailed Cards */
                                <>
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                        <Clock className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-foreground">Takes 5-10 Minutes</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Processing takes longer for thorough analysis and better results</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-foreground">Higher Quality Output</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">More polished UI with cleaner code and better design</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                        <Shield className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-foreground">Fewer Errors</p>
                                            <p className="text-xs text-muted-foreground mt-0.5">Reduced bugs and more reliable implementations</p>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Action buttons */}
                        <DialogFooter className="flex-col gap-2 sm:flex-col">
                            <div className="flex flex-col sm:flex-row gap-2 w-full">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        // User chooses to stay in normal mode
                                        if (pendingMessage !== null) {
                                            // Send the pending message in normal mode
                                            doSend(pendingMessage, pendingImageFile, pendingNetworkErrors);
                                        }
                                        setIsAdvancedModeDialogOpen(false);
                                        commonService.handleMixpanelTrack("advanced_mode_prompt_stay_normal", {
                                            project_id: projectId,
                                            had_pending_message: pendingMessage !== null,
                                            location: "chat_input"
                                        });
                                    }}
                                    className="flex-1 order-2 sm:order-1 h-9 sm:h-10 text-xs sm:text-sm"
                                >
                                    Stay in Normal Mode
                                </Button>
                                <Button
                                    variant="default"
                                    onClick={() => {
                                        // Enable advanced mode
                                        setIsAdvancedModeEnabled(true);
                                        onAdvancedModeChange?.(true);

                                        // Set flag to send message after mode change propagates
                                        // The useEffect will handle sending once isAdvancedModeEnabled becomes true
                                        if (pendingMessage !== null) {
                                            setShouldSendAfterModeChange(true);
                                        }

                                        setIsAdvancedModeDialogOpen(false);
                                        commonService.handleMixpanelTrack("advanced_mode_prompt_enabled", {
                                            project_id: projectId,
                                            had_pending_message: pendingMessage !== null,
                                            location: "chat_input"
                                        });
                                    }}
                                    className="flex-1 gap-2 bg-primary hover:bg-primary/90 order-1 sm:order-2 h-9 sm:h-10 text-xs sm:text-sm"
                                >
                                    <Zap className={cn("w-4 h-4", isMobile ? "w-3.5 h-3.5" : "")} />
                                    Enable Advanced Mode
                                </Button>
                            </div>
                            <Button
                                variant="text"
                                size="sm"
                                onClick={() => {
                                    // Dismiss the prompt for this project
                                    const dismissalKey = projectId ? `${Misc.LOCAL_STORAGE_ADVANCED_MODE_PROMPT_DISMISSED}${projectId}` : null;
                                    if (dismissalKey) {
                                        localStorage.setItem(dismissalKey, 'true');
                                        setIsAdvancedModePromptDismissed(true);
                                    }

                                    // Send the pending message in normal mode
                                    if (pendingMessage !== null) {
                                        doSend(pendingMessage, pendingImageFile, pendingNetworkErrors);
                                    }

                                    setIsAdvancedModeDialogOpen(false);
                                    commonService.handleMixpanelTrack("advanced_mode_prompt_dismissed", {
                                        project_id: projectId,
                                        had_pending_message: pendingMessage !== null,
                                        location: "chat_input"
                                    });
                                }}
                            >
                                Don't ask again (for this project)
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContentNoClose>
            </Dialog>
        </>
    );
};

export default CustomInputComponent;